import styled from "@emotion/styled";
import { darkTheme } from "@avenue-8/sidekick-ui";
import { Box, BoxProps, Skeleton, Typography } from "@mui/material";
import { Image } from "@shared/components/Image";
import { formatSecondsLabel } from "@shared/utils/formatSecondsLabel";
import { useCallback } from "react";

const Card = styled(Box)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.75rem;
  color: ${(p) => p.theme.text.colors.primary};
  background-color: ${(p) => p.theme.colors.background};
  /* box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2); */
  border: 1px solid ${(p) => p.theme.colors.border2};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  width: 100%;
  min-height: 164px;

  &:hover {
    /* transform: scale(1.02); */
    /* box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.2); */
    border-color: ${(p) => p.theme.colors.border};
  }

  &[aria-disabled="true"] {
    opacity: 0.4;
    pointer-events: none;
  }
`;

const CardHeader = styled.div`
  width: 100%;
  height: 180px;
  pointer-events: none;
  overflow: hidden;
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.1);
    z-index: 1;
    pointer-events: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const VideoDuration = styled(Typography)`
  display: inline;
  position: absolute;
  top: -2.25rem;
  right: 0.75rem;
  background-color: ${() => darkTheme.colors.background};
  color: #fff;
  font-weight: 600;
  font-size: ${(p) => p.theme.text.fontSizes.xs};
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  z-index: 2;
`;

const CardTitle = styled(Typography)`
  color: ${(p) => p.theme.text.colors.primary};
  font-family: ${(p) => p.theme.title.fonts.primary};
  font-size: ${(p) => p.theme.text.fontSizes.m};
  font-weight: 500;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  width: 100%;
  flex: 1;
  padding: 1rem;
`;

interface Props extends BoxProps {
  videoId: string;
  videoTitle: string;
  videoThumbnailUrl: string;
  videoDuration: number;
  onClickCard: (params: { videoId: string; videoTitle: string }) => void;
}

export const VideoCard = (props: Props) => {
  const {
    onClickCard,
    videoTitle,
    videoThumbnailUrl,
    videoDuration,
    videoId,
    ...rest
  } = props;

  const handleClickCard = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      onClickCard({ videoId, videoTitle });
    },
    [onClickCard, videoId, videoTitle]
  );

  return (
    <Card key={videoId} {...rest} onClick={handleClickCard}>
      <CardHeader>
        <Image src={videoThumbnailUrl} />
      </CardHeader>
      <CardContent>
        <CardTitle>{videoTitle}</CardTitle>
        <VideoDuration variant="body2">
          {formatSecondsLabel(videoDuration ?? 0)}
        </VideoDuration>
      </CardContent>
    </Card>
  );
};

export const VideoCardSkeleton = () => {
  return (
    <Card>
      <CardHeader>
        <Skeleton variant="rectangular" height={"100%"} />
      </CardHeader>
      <CardContent>
        <Skeleton width="100%" variant="text" />
      </CardContent>
    </Card>
  );
};
